/**
 * Client-side showIf evaluator. Mirrors the backend's payload-validator
 * semantics so the renderer's visibility decisions match what the server
 * accepts on submit.
 */

import type { FormSchema, ShowIfCondition } from '@school-journey/shared-types/schemas'

export function evaluateShowIf(
  condition: ShowIfCondition,
  payload: Record<string, unknown>,
  visibleIds: Set<string>
): boolean {
  if (!visibleIds.has(condition.fieldId)) return false
  const value = payload[condition.fieldId]

  switch (condition.op) {
    case 'equals':
      return typeof condition.value === 'string' && value === condition.value
    case 'not_equals':
      return typeof condition.value === 'string' && value !== condition.value
    case 'in': {
      if (!Array.isArray(condition.value)) return false
      return typeof value === 'string' && condition.value.includes(value)
    }
    case 'not_empty':
      if (value === undefined || value === null || value === '') return false
      if (Array.isArray(value) && value.length === 0) return false
      return true
    default:
      return false
  }
}

/**
 * Compute the set of visible block ids in declaration order against the
 * current payload. Used by the renderer to decide what to render and what
 * to send.
 */
export function computeVisibleBlockIds(schema: FormSchema, payload: Record<string, unknown>): Set<string> {
  const visible = new Set<string>()
  for (const block of schema.content) {
    if (block.type === 'PageBreak' || block.type === 'SectionHeader' || block.type === 'Markdown') {
      continue
    }
    const id = block.props.id
    const showIf = 'showIf' in block.props ? block.props.showIf : undefined
    if (showIf && !evaluateShowIf(showIf, payload, visible)) continue
    visible.add(id)
  }
  return visible
}

/**
 * Split schema content into pages, with PageBreak blocks acting as boundaries.
 * The first page is whatever appears before the first PageBreak (may be empty
 * if the form opens with one).
 */
export function splitSchemaIntoPages(schema: FormSchema): Array<{
  title: string | undefined
  blocks: FormSchema['content']
}> {
  const pages: Array<{ title: string | undefined; blocks: FormSchema['content'] }> = []
  let currentTitle: string | undefined = undefined
  let currentBlocks: FormSchema['content'] = []

  for (const block of schema.content) {
    if (block.type === 'PageBreak') {
      pages.push({ title: currentTitle, blocks: currentBlocks })
      currentTitle = block.props.title
      currentBlocks = []
    } else {
      currentBlocks.push(block)
    }
  }
  pages.push({ title: currentTitle, blocks: currentBlocks })

  // Drop a leading empty page if the schema started with a PageBreak.
  if (pages.length > 1 && pages[0].blocks.length === 0) pages.shift()

  return pages
}
